import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import MicIcon from "@mui/icons-material/Mic";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { addAgentMessage, setAgentStatus, resetAgent, setPegaConversationID } from "../features/channelSlice";
import { startConversation, sendMessage, closeConversation } from '../utils/pegaApi';
import authService from '../services/authService';
// Removed import of getBackendBaseURL - using backendBaseURL from Redux state instead

// Safe audio creation with error handling
const createSafeAudio = (src) => {
  if (typeof Audio === 'undefined') return null;
  try {
    const audio = new Audio(src);
    // Preload to check if the file exists
    audio.preload = 'metadata';
    audio.addEventListener('error', (e) => {
      console.debug(`Audio file not available: ${src}`);
    });
    audio.addEventListener('canplaythrough', () => {
      console.debug(`Audio file loaded successfully: ${src}`);
    });
    return audio;
  } catch (error) {
    console.debug(`Failed to create audio element for: ${src}`, error);
    return null;
  }
};

// Only create audio objects if sound effects are enabled (can be configured via config)
const processingSound = createSafeAudio('/sounds/processing.mp3');
const doneSound = createSafeAudio('/sounds/done.mp3');
const beepSound = createSafeAudio('/sounds/beep.mp3');

// Removed darkenColor function as it was unused
const AgentViewStyles = ({ gradientColor1, gradientColor2, gradientColor3, gradientColor4, backgroundColor, conversationBubbleBackgroundColor, conversationBubbleForegroundColor }) => (
  <style>{`
    :root {
      --agent-primary: ${gradientColor1 || '#8b5cf6'};
      --agent-secondary: ${gradientColor2 || '#06b6d4'};
      --agent-accent: ${gradientColor3 || '#ec4899'};
      --agent-quaternary: ${gradientColor4 || '#23d5ab'};
      --agent-glass: rgba(15, 23, 42, 0.8);
      --agent-text: #f1f5f9;
      --agent-text-dim: #94a3b8;
    }

    .agent-view-glass {
      background: ${backgroundColor || 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.9) 50%, rgba(15, 23, 42, 0.95) 100%)'};
      backdrop-filter: blur(20px);
    }

    /* Subtle animated gradient background shimmer */
    .agent-view-glass::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(ellipse at 50% 0%, var(--agent-primary)08 0%, transparent 50%),
                  radial-gradient(ellipse at 80% 100%, var(--agent-secondary)06 0%, transparent 40%),
                  radial-gradient(ellipse at 20% 80%, var(--agent-accent)05 0%, transparent 40%);
      pointer-events: none;
      z-index: 0;
    }

    /* Custom scrollbar styles removed - using no-scrollbar class instead */

    /* Small orb animations for conversation UI */
    @keyframes small-breathe {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }

    @keyframes small-ring-pulse {
      0%, 100% { transform: scale(1); opacity: 0.6; }
      50% { transform: scale(1.3); opacity: 0.2; }
    }

    @keyframes small-glow-pulse {
      0%, 100% { opacity: 0.4; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.2); }
    }

    /* Smooth fade-in animation for messages */
    @keyframes message-slide-in {
      0% { 
        opacity: 0; 
        transform: translateY(12px) scale(0.98); 
      }
      100% { 
        opacity: 1; 
        transform: translateY(0) scale(1); 
      }
    }

    .animate-fade-in {
      animation: message-slide-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }

    .mic-button {
      position: relative;
      width: 44px;
      height: 44px;
      border-radius: 12px;
      border: 2px solid rgba(148, 163, 184, 0.3);
      background: linear-gradient(135deg, 
        rgba(15, 23, 42, 0.9) 0%, 
        rgba(30, 41, 59, 0.8) 100%);
      backdrop-filter: blur(10px);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .mic-button::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      border-radius: 50%;
      background: linear-gradient(45deg, transparent, var(--agent-primary), transparent);
      z-index: -1;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .mic-button.is-recording {
      background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
      border-color: #ef4444;
      transform: scale(1.1);
      box-shadow: 0 0 30px rgba(239, 68, 68, 0.5);
    }

    .mic-button.is-recording::before {
      opacity: 1;
      background: linear-gradient(45deg, transparent, #ef4444, transparent);
      animation: button-glow 2s linear infinite;
    }

    .mic-button.is-idle:hover {
      background: linear-gradient(135deg, 
        rgba(148, 163, 184, 0.4) 0%, 
        rgba(100, 116, 139, 0.5) 50%, 
        rgba(71, 85, 105, 0.6) 100%);
      border-color: rgba(148, 163, 184, 0.6);
      transform: scale(1.05);
      box-shadow: 0 12px 40px rgba(148, 163, 184, 0.2);
    }

    .mic-button.is-idle:hover::before {
      opacity: 1;
      background: linear-gradient(45deg, transparent, rgba(148, 163, 184, 0.4), transparent);
    }

    .mic-button.is-disabled {
      background: linear-gradient(135deg, 
        rgba(71, 85, 105, 0.6) 0%, 
        rgba(51, 65, 85, 0.6) 100%);
      border-color: rgba(100, 116, 139, 0.4);
      cursor: not-allowed;
    }

    @keyframes button-glow {
      0%, 100% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .nav-button {
      position: relative;
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: linear-gradient(135deg, 
        rgba(30, 41, 59, 0.9) 0%, 
        rgba(15, 23, 42, 0.95) 100%);
      border: 1px solid rgba(148, 163, 184, 0.2);
      backdrop-filter: blur(10px);
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2),
                  inset 0 1px 0 rgba(255, 255, 255, 0.05);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    .nav-button::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, 
        var(--agent-primary)00 0%, 
        var(--agent-primary)20 50%,
        var(--agent-secondary)15 100%);
      opacity: 0;
      transition: opacity 0.25s ease;
    }

    .nav-button:hover {
      border-color: var(--agent-primary)50;
      transform: translateY(-2px) scale(1.02);
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.3),
                  0 0 20px var(--agent-primary)15;
    }

    .nav-button:hover::before {
      opacity: 1;
    }

    .nav-button:active {
      transform: translateY(0) scale(0.98);
      transition: transform 0.1s ease;
    }

    .response-container {
      background: linear-gradient(135deg, 
        rgba(15, 23, 42, 0.95) 0%, 
        rgba(30, 41, 59, 0.9) 50%, 
        rgba(15, 23, 42, 0.95) 100%);
      border: 1px solid rgba(148, 163, 184, 0.2);
      backdrop-filter: blur(20px);
      border-radius: 24px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    }

    .agent-animated-text {
      text-align: left !important;
    }

    .agent-animated-text .word {
      opacity: 0;
      transition: opacity 0.6s ease-in-out;
      display: inline;
      color: inherit;
      text-align: left;
    }

    .agent-animated-text .word.visible {
      opacity: 1;
    }

    /* Fallback for any word elements */
    .word {
      opacity: 0;
      transition: opacity 0.6s ease-in-out;
      display: inline;
      text-align: left;
    }

    .word.visible {
      opacity: 1;
    }

    .footer-glass {
      background: linear-gradient(180deg, 
        rgba(15, 23, 42, 0.92) 0%, 
        rgba(15, 23, 42, 0.98) 100%);
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
      border-top: 1px solid rgba(148, 163, 184, 0.12);
      box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
      padding: 12px 0;
      min-height: 80px;
    }

    .conversation-bubble {
      background: ${conversationBubbleBackgroundColor || 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)'};
      color: ${conversationBubbleForegroundColor || '#f1f5f9'};
      border: 1px solid rgba(148, 163, 184, 0.15);
      backdrop-filter: blur(12px);
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25), 
                  0 1px 3px rgba(0, 0, 0, 0.15),
                  inset 0 1px 0 rgba(255, 255, 255, 0.05);
      border-radius: 1.25rem;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .conversation-bubble:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 28px rgba(0, 0, 0, 0.3), 
                  0 2px 4px rgba(0, 0, 0, 0.15),
                  inset 0 1px 0 rgba(255, 255, 255, 0.08);
    }

    /* User message bubble accent */
    .conversation-bubble.user-bubble {
      background: linear-gradient(135deg, 
        ${gradientColor1 || '#8b5cf6'}18 0%, 
        ${gradientColor2 || '#06b6d4'}12 100%);
      border-color: ${gradientColor1 || '#8b5cf6'}25;
    }

    .text-input-overlay {
      background: linear-gradient(180deg, 
        rgba(15, 23, 42, 0.98) 0%, 
        rgba(15, 23, 42, 0.99) 100%);
      backdrop-filter: blur(30px);
    }

    /* Text selection colors - use agent brand color */
    .agent-view-glass ::selection {
      background-color: ${gradientColor1 || '#8b5cf6'}40;
      color: #ffffff;
    }

    .agent-view-glass ::-moz-selection {
      background-color: ${gradientColor1 || '#8b5cf6'}40;
      color: #ffffff;
    }

    .agent-animated-text ::selection {
      background-color: ${gradientColor1 || '#8b5cf6'}40;
      color: #ffffff;
    }

    .agent-animated-text ::-moz-selection {
      background-color: ${gradientColor1 || '#8b5cf6'}40;
      color: #ffffff;
    }

    .conversation-bubble ::selection {
      background-color: ${gradientColor1 || '#8b5cf6'}40;
      color: #ffffff;
    }

    .conversation-bubble ::-moz-selection {
      background-color: ${gradientColor1 || '#8b5cf6'}40;
      color: #ffffff;
    }

    .sender-name {
      text-transform: uppercase;
      letter-spacing: 0.12em;
      font-size: 0.7rem;
      background: linear-gradient(90deg, var(--agent-text-dim) 0%, var(--agent-text) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .conversation-fade-overlay {
      position: absolute;
      bottom: -35px;
      left: 0;
      right: 0;
      height: 40px;
      background: linear-gradient(to top, 
        transparent 0%, 
        ${backgroundColor || 'rgba(15, 23, 42, 0.95)'} 100%);
      pointer-events: none;
      z-index: 30;
    }
  `}</style>
);

// Small orb component for agent name area - only animates when thinking
function SmallAgentOrb({ isThinking, logoSrc }) {
  return (
    <div 
      className={`inline-flex items-center justify-center ${isThinking ? 'is-thinking' : ''}`}
      style={{
        width: '20px',
        height: '20px',
        position: 'relative',
        marginRight: '6px',
        flexShrink: 0,
      }}
    >
      {isThinking && (
        <>
          {/* Animated glow rings for thinking state */}
          <div 
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '1.5px solid',
              borderColor: 'var(--agent-primary)',
              opacity: 0.6,
              animation: 'small-ring-pulse 2s ease-in-out infinite',
            }}
          />
          <div 
            style={{
              position: 'absolute',
              width: '120%',
              height: '120%',
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'var(--agent-secondary)',
              opacity: 0.4,
              animation: 'small-ring-pulse 2s ease-in-out infinite 0.3s',
            }}
          />
        </>
      )}
      
      {/* Core orb */}
      <div 
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: isThinking 
            ? 'linear-gradient(135deg, var(--agent-primary) 0%, var(--agent-secondary) 50%, var(--agent-accent) 100%)'
            : 'linear-gradient(135deg, rgba(148, 163, 184, 0.4) 0%, rgba(100, 116, 139, 0.5) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isThinking 
            ? '0 0 12px var(--agent-primary)66, inset 0 0 6px rgba(255, 255, 255, 0.1)'
            : '0 2px 8px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.4s ease',
          animation: isThinking ? 'small-breathe 3s ease-in-out infinite' : 'none',
          zIndex: 2,
        }}
      >
        {logoSrc ? (
          <img 
            src={logoSrc} 
            alt="Agent" 
            style={{ 
              width: '14px',
              height: '14px',
              objectFit: 'contain',
              borderRadius: '50%',
              filter: 'brightness(1.1) contrast(1.1)',
            }}
          />
        ) : (
          <div 
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.8)',
            }}
          />
        )}
      </div>
      
      {isThinking && (
        <div 
          style={{
            position: 'absolute',
            width: '140%',
            height: '140%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, var(--agent-primary)33 0%, transparent 70%)',
            filter: 'blur(8px)',
            animation: 'small-glow-pulse 4s ease-in-out infinite',
            zIndex: 0,
          }}
        />
      )}
    </div>
  );
}

// Header with futuristic styling
function Header({ gradientColor1, gradientColor2, gradientColor3, foregroundColor }) {
  const { channelConfig } = useSelector((state) => state.channel);
  // Always use agent channel config for agent title and subtitle
  const meta = channelConfig.agent || {};

  return (
    <div className="text-center">
      <h1 className="text-2xl font-bold mb-1"
          style={{ 
            color: foregroundColor || '#ffffff',
            textShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
            letterSpacing: '-0.02em',
          }}>
        {meta.title || 'AI Agent'}
      </h1>
      {meta.subtitle && (
        <p className="text-sm font-light tracking-wide"
           style={{ 
             color: foregroundColor || '#94a3b8',
             opacity: 0.85,
             letterSpacing: '0.05em',
           }}>
          {meta.subtitle}
        </p>
      )}
    </div>
  );
}

// Futuristic MicrophoneButton component
function MicrophoneButton({ onStart, onStop, isRecording, isSpeaking, isPlaying, onStopAudio, isThinking, isAnimatingText, onFastForward, isListening }) {
  let buttonClass = 'mic-button';
  if (isRecording || isListening) buttonClass += ' is-recording'; // Treat listening same as recording visually
  else if (isThinking) buttonClass += ' is-disabled';
  else buttonClass += ' is-idle';

  if (isAnimatingText) {
    return (
      <button onClick={onFastForward} className={buttonClass} aria-label="Fast Forward">
        <span className="material-icons-outlined text-white" style={{ fontSize: 24 }}>fast_forward</span>
      </button>
    );
  }

  if (isPlaying) {
    return (
      <button onClick={onStopAudio} className={buttonClass} aria-label="Fast Forward">
        <span className="material-icons-outlined text-white" style={{ fontSize: 24 }}>fast_forward</span>
      </button>
    );
  }
  
  return (
    <button 
      onClick={(isRecording || isListening) ? onStop : onStart} 
      className={buttonClass} 
      aria-label={(isRecording || isListening) ? 'Stop recording' : 'Start recording'}
      disabled={isThinking}
      style={{
        opacity: isThinking ? 0.5 : 1,
        cursor: isThinking ? 'not-allowed' : 'pointer'
      }}
    >
      <MicIcon sx={{ fontSize: '24px', color: 'white' }} />
    </button>
  );
}

// Export the classic AgentView as default
export default function AgentView({ isEnvironmentOffline = false }) {
  const dispatch = useDispatch();
  const {
    agentStatus,
    agentMessages,
    configId,
    channelConfig,
    pegaContextID,
    pegaConversationID,
    currentChannel,
    previousChannel,
    backendBaseURL,
    enableSoundEffects,
    currentBrand,
  } = useSelector((state) => state.channel);

  // Resolve TTS voice and accent from brand config, falling back to null (backend defaults)
  const brandTtsVoice = currentBrand?.brand_json?.voiceId || currentBrand?.voiceId || null;
  const brandTtsAccent = currentBrand?.brand_json?.voiceAccent || currentBrand?.voiceAccent || null;
  
  const agentConfig = channelConfig.agent;
  const agentTitle = agentConfig?.title || 'Agent';
  const agentFontFamily = agentConfig?.fontFamily || '';
  const gradientColor1 = agentConfig?.gradientColor1 || '#ee7752';
  const gradientColor2 = agentConfig?.gradientColor2 || '#e73c7e';
  const gradientColor3 = agentConfig?.gradientColor3 || '#23a6d5';
  const gradientColor4 = agentConfig?.gradientColor4 || '#23d5ab';
  const backgroundColor = agentConfig?.backgroundColor || null;
  const foregroundColor = agentConfig?.foregroundColor || null;
  const conversationBubbleBackgroundColor = agentConfig?.conversationBubbleBackgroundColor || null;
  const conversationBubbleForegroundColor = agentConfig?.conversationBubbleForegroundColor || null;

  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const useVoiceRecording = (onStopRecording, audioContextRef) => {
    const [isRecording, setIsRecording] = useState(false);
    const [permissionError, setPermissionError] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const silenceTimeoutRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);

    const SILENCE_DURATION = 1500; // ms
    const SILENCE_THRESHOLD = 0.01; // Adjust as needed

    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const source = audioContextRef.current.createMediaStreamSource(stream);
        sourceRef.current = source;
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;
        source.connect(analyserRef.current);
        mediaRecorderRef.current = new MediaRecorder(stream);

        mediaRecorderRef.current.ondataavailable = (event) => {
          audioChunksRef.current.push(event.data);
        };

        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          setIsRecording(false);
          onStopRecording(audioBlob);
          audioChunksRef.current = [];
          stream.getTracks().forEach(track => track.stop());
          if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);
        setPermissionError(false);
        detectSilence();
      } catch (error) {
        console.error("Error accessing microphone:", error);
        setPermissionError(true);
        setIsRecording(false);
      }
    };

    const stopRecording = () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
    };

    // Silence detection logic
    const detectSilence = useCallback(() => {
      if (!analyserRef.current) return;
      const buffer = new Float32Array(analyserRef.current.fftSize);
      analyserRef.current.getFloatTimeDomainData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) {
        sum += buffer[i] * buffer[i];
      }
      const rms = Math.sqrt(sum / buffer.length);
      if (rms < SILENCE_THRESHOLD) {
        if (!silenceTimeoutRef.current) {
          silenceTimeoutRef.current = setTimeout(() => {
            stopRecording();
          }, SILENCE_DURATION);
        }
      } else {
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
      }
      if (isRecording) {
        requestAnimationFrame(detectSilence);
      }
    }, [isRecording]);

    useEffect(() => {
      if (isRecording) {
        detectSilence();
      }
      return () => {
        if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      };
    }, [isRecording, detectSilence]);

    return {
      isRecording,
      permissionError,
      startRecording,
      stopRecording,
    };
  };

  const handleStopRecording = (audioBlob) => {
    setShowFullText(false);
    // Don't show "Thinking" here - let sendAudioToServer handle it after user message is displayed
    sendAudioToServer(audioBlob);
  };

  const { isRecording, permissionError, startRecording, stopRecording } = useVoiceRecording(
    handleStopRecording,
    audioContextRef
  );

  const isListening = agentStatus === 'listening';
  const isThinking = agentStatus === 'thinking';
  const isSpeaking = agentStatus === 'speaking';
  const [showFullText, setShowFullText] = useState(true);
  const [textInput, setTextInput] = useState('');
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);
  
  // Ref for text input field
  const textareaRef = useRef(null);
  
  // Inline AnimatedResponse refs and state
  const animatedResponseRef = useRef();
  const animatedTimeoutsRef = useRef([]);
  const lastAnimatedTextRef = useRef();

  // Persist last agent message and animation state across navigation
  const lastAgentMessageRef = useRef(null);
  const showFullTextRef = useRef(false);

  // Unified conversation scroll ref
  const conversationScrollRef = useRef();

  // On mount, restore last agent message and animation state if available
  useEffect(() => {
    if (currentChannel === 'agent') {
      // Always show last agent message in full when entering agent channel
      setShowFullText(true);
    } else {
      setShowFullText(true);
    }
    // Reset mic/animation state on mount
    if (isRecording) {
      // If somehow still recording, stop
      dispatch(setAgentStatus({ agentStatus: 'idle' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChannel]);

  // On unmount, persist last agent message and animation state, reset mic/animation state
  useEffect(() => {
    return () => {
      lastAgentMessageRef.current = agentMessages.filter(m => m.sender === 'agent').pop() || null;
      showFullTextRef.current = showFullText;
      setShowFullText(false);
      if (isRecording) {
        dispatch(setAgentStatus({ agentStatus: 'idle' }));
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect navigation to agent channel from another channel
  useEffect(() => {
    if (currentChannel === 'agent' && previousChannel && previousChannel !== 'agent') {
      // Show last agent message in full, no animation/audio
      setShowFullText(true);
    }
  }, [currentChannel, previousChannel]);

  const playAudio = (audioContent) => {
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
      } catch (e) {}
    }

    const play = async () => {
      try {
        if (!audioContextRef.current) return;
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }
        const audioData = atob(audioContent);
        const audioBuffer = await audioContextRef.current.decodeAudioData(
          Uint8Array.from(audioData, c => c.charCodeAt(0)).buffer
        );
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContextRef.current.destination);
        source.onended = () => {
          dispatch(setAgentStatus({ agentStatus: 'idle' }));
          setShowFullText(true);
          audioSourceRef.current = null;
        };
        audioSourceRef.current = source;
        source.start(0);
      } catch (error) {
        console.error("Error playing audio:", error);
        dispatch(setAgentStatus({ agentStatus: 'idle' }));
        setShowFullText(true);
      }
    };
    play();
  };

  const handleStopAudio = () => {
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
      } catch (e) {}
    }
  };

  const sendAudioToServer = async (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    
    try {
      const apiBaseURL = backendBaseURL;
      
      // Get JWT token for authentication
      const token = await authService.getToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${apiBaseURL}/api/v1/speech-to-text`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });
      const data = await response.json();
      // Strip any timing artefacts from the STT result (e.g. "[00:00]", "(0:01 - 0:02)")
      const cleanTranscript = (data.transcript || '')
        .replace(/\[?\(?\d{1,2}:\d{2}(?:\s*[-–]\s*\d{1,2}:\d{2})?\)?\]?/g, '')
        .trim();
      if (response.ok && cleanTranscript) {
        // Immediately stop listening status to remove the "Listening" bubble
        dispatch(setAgentStatus({ agentStatus: 'idle' }));
        // Add the user message with the transcript only if transcript is not empty
        dispatch(addAgentMessage({ sender: 'user', text: cleanTranscript }));
        // Wait for user message to render before starting agent response
        setTimeout(() => {
          getAndPlayResponse(cleanTranscript);
        }, 400);
      } else {
        // No transcript extracted - just set to idle without showing any message
        dispatch(setAgentStatus({ agentStatus: 'idle' }));
      }
    } catch (error) {
      console.error('Error sending audio:', error);
      dispatch(setAgentStatus({ agentStatus: 'idle' }));
      // Show user-friendly error message
      dispatch(addAgentMessage({
        sender: 'agent',
        text: "Sorry, I'm having problems understanding right now."
      }));
    }
  };

  const getTextResponse = async (transcript) => {
    setShowFullText(false);
    dispatch(addAgentMessage({ sender: 'user', text: transcript })); // Add user message
    
    // Check configuration before setting thinking status
    if (!agentConfig || !agentConfig.agentID) {
        dispatch(setAgentStatus({ agentStatus: 'idle' }));
        dispatch(addAgentMessage({ sender: 'agent', text: "The agent is not configured. Please set the Agent ID and authentication details in the configuration." }));
        return;
    }

    // Add a slight delay before showing the "Thinking" bubble
    const thinkingTimeout = setTimeout(() => {
      dispatch(setAgentStatus({ agentStatus: 'thinking' }));
    }, 400);

    try {
        let conversationID = pegaConversationID;
        let agentResponse;

        const startAndSendMessage = async () => {
            const newConversation = await startConversation(agentConfig.agentID, pegaContextID, configId);
            dispatch(setPegaConversationID(newConversation.ID));
            return sendMessage(agentConfig.agentID, newConversation.ID, transcript, configId);
        };

        if (conversationID) {
            try {
                agentResponse = await sendMessage(agentConfig.agentID, conversationID, transcript, configId);
            } catch (error) {
                console.warn('sendMessage failed, attempting to start a new conversation.', error);
                agentResponse = await startAndSendMessage();
            }
        } else {
            agentResponse = await startAndSendMessage();
        }

        const responseText = agentResponse.response;

        dispatch(addAgentMessage({
            sender: 'agent',
            text: responseText,
        }));
        dispatch(setAgentStatus({ agentStatus: 'speaking' }));

        // Text-only responses are 2x faster (100ms per word vs 200ms)
        const BASE_WORD_INTERVAL = 200;
        const wordInterval = BASE_WORD_INTERVAL / 2; // 100ms for text-only
        const animationDuration = responseText.split(' ').length * wordInterval;
        setTimeout(() => {
            dispatch(setAgentStatus({ agentStatus: 'idle' }));
        }, animationDuration);

    } catch (error) {
      console.error('Error in conversation flow:', error);
      // Clear the thinking timeout and immediately set to idle
      clearTimeout(thinkingTimeout);
      dispatch(setAgentStatus({ agentStatus: 'idle' }));
      // Show user-friendly error message
      dispatch(addAgentMessage({
        sender: 'agent',
        text: "Sorry, I'm having problems understanding right now."
      }));
    }
  };

  const getAndPlayResponse = async (transcript) => {
    setShowFullText(false);
    
    // Check configuration before setting thinking status
    if (!agentConfig || !agentConfig.agentID) {
      dispatch(setAgentStatus({ agentStatus: 'idle' }));
      dispatch(addAgentMessage({ sender: 'agent', text: "The agent is not configured. Please set the Agent ID and authentication details in the configuration." }));
      return;
    }
    
    dispatch(setAgentStatus({ agentStatus: 'thinking' }));
  
    try {
      let conversationID = pegaConversationID;
      let agentResponse;
  
      // Helper function to start a new conversation and send the first message
      const startAndSendMessage = async () => {
        const newConversation = await startConversation(agentConfig.agentID, pegaContextID, configId);
        dispatch(setPegaConversationID(newConversation.ID));
        return sendMessage(agentConfig.agentID, newConversation.ID, transcript, configId);
      };
  
      if (conversationID) {
        try {
          agentResponse = await sendMessage(agentConfig.agentID, conversationID, transcript, configId);
        } catch (error) {
          console.warn('sendMessage failed, attempting to start a new conversation.', error);
          agentResponse = await startAndSendMessage();
        }
      } else {
        agentResponse = await startAndSendMessage();
      }
  
      const responseText = agentResponse.response;
  
      // Get audio for the response
      const apiBaseURL = backendBaseURL;
      
      // Get JWT token for authentication
      const token = await authService.getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const ttsResponse = await fetch(`${apiBaseURL}/api/v1/text-to-speech`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          text: responseText,
          ...(brandTtsVoice ? { model: brandTtsVoice } : {}),
          ...(brandTtsAccent ? { voice_accent: brandTtsAccent } : {}),
        }),
      });
  
      if (!ttsResponse.ok) {
        const errorBody = await ttsResponse.text();
        console.error('Failed to get audio from TTS service:', errorBody);
        throw new Error('Failed to get audio from TTS service.');
      }
  
      const ttsData = await ttsResponse.json();
      dispatch(addAgentMessage({
        sender: 'agent',
        text: responseText,
        audioContent: ttsData.audioContent,
      }));
      dispatch(setAgentStatus({ agentStatus: 'speaking' }));
      playAudio(ttsData.audioContent);
  
    } catch (error) {
      console.error('Error in conversation flow:', error);
      dispatch(setAgentStatus({ agentStatus: 'idle' }));
      // Show user-friendly error message
      dispatch(addAgentMessage({
        sender: 'agent',
        text: "Sorry, I'm having problems understanding right now."
      }));
    }
  };

  const lastAgentMessage = agentMessages.filter(m => m.sender === 'agent').pop();

  // Helper function to scroll to bottom smoothly
  const scrollToBottom = useCallback((smooth = false) => {
    if (conversationScrollRef.current) {
      const scrollOptions = {
        top: conversationScrollRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      };
      conversationScrollRef.current.scrollTo(scrollOptions);
    }
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    // Don't auto-scroll if we're currently animating a response
    const isAnimating = isSpeaking && lastAgentMessage && !lastAgentMessage.audioContent;
    
    console.log('⬇️ Auto-scroll effect triggered:', {
      messagesLength: agentMessages.length,
      isAnimating: isAnimating,
      isSpeaking: isSpeaking,
      agentStatus: agentStatus,
      lastMessageHasAudio: lastAgentMessage?.audioContent ? 'yes' : 'no',
      willScroll: !isAnimating
    });
    
    if (isAnimating) {
      console.log('⚠️ Skipping auto-scroll - animation in progress');
      return; // Don't auto-scroll during word-by-word animation
    }
    
    // Also skip if we just started speaking (wait for animation to position first)
    if (isSpeaking && agentStatus === 'speaking') {
      console.log('⚠️ Skipping auto-scroll - just started speaking, animation will control scroll');
      return;
    }
    
    // Use requestAnimationFrame to ensure DOM has fully updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        console.log('✅ Executing auto-scroll to bottom');
        scrollToBottom(false);
      });
    });
  }, [agentMessages.length, scrollToBottom, isSpeaking, lastAgentMessage, agentStatus]);

  const [thinkingStatus, setThinkingStatus] = useState('Thinking');
  useEffect(() => {
    let timer;
    let isMounted = true;
    function loopStatus(current) {
      if (!isMounted || !isThinking) return;
      if (current === 'Thinking') {
        setThinkingStatus('Working');
        timer = setTimeout(() => loopStatus('Working'), 2000);
      } else {
        setThinkingStatus('Thinking');
        timer = setTimeout(() => loopStatus('Thinking'), 5000);
      }
    }
    if (isThinking) {
      setThinkingStatus('Thinking');
      timer = setTimeout(() => loopStatus('Thinking'), 2000);
    }
    return () => {
      isMounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [isThinking]);

  // Handler to start recording: always stop previous audio first
  const handleStartRecording = () => {
    if (audioSourceRef.current) {
        audioSourceRef.current.stop();
    }
    setShowFullText(false); // Hide full text for new animation
    dispatch(setAgentStatus({ agentStatus: 'listening' }));
    
    // Don't add a message here - just show the listening status bubble
    startRecording();
  };

  const agentStopRequest = useSelector((state) => state.channel.agentStopRequest);
  useEffect(() => {
    if (agentStopRequest && (isRecording || isSpeaking)) {
      // Stop audio and recording
      if (audioSourceRef.current) {
        audioSourceRef.current.stop();
      }
      if (isRecording && stopRecording) {
        stopRecording();
      }
      dispatch(setAgentStatus({ agentStatus: 'idle' }));
      // After stopping, navigate if requested
      if (agentStopRequest.nextChannel && agentStopRequest.nextChannel !== currentChannel) {
        dispatch({ type: 'channel/setChannel', payload: agentStopRequest.nextChannel });
      }
      dispatch({ type: 'channel/clearAgentStopRequest' });
    }
  }, [agentStopRequest, isRecording, isSpeaking, stopRecording, dispatch, currentChannel]);

  // On mount and when currentChannel changes, always show last agent message in full if on agent channel
  useEffect(() => {
    if (currentChannel === 'agent') {
      setShowFullText(true);
    } else {
      setShowFullText(true);
    }
    // Reset mic/animation state on mount
    if (isRecording) {
      if (audioSourceRef.current) {
        audioSourceRef.current.stop();
      }
      dispatch(setAgentStatus({ agentStatus: 'idle' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChannel]);

  const isAnimatingText = isSpeaking && lastAgentMessage && !lastAgentMessage.audioContent;



  // Play processing/done sounds on agentStatus change (only if sound effects are enabled and online)
  const prevAgentStatus = useRef(agentStatus);
  useEffect(() => {
    const playSound = async (audioElement) => {
      if (!audioElement || !enableSoundEffects || isEnvironmentOffline) return;
      try {
        audioElement.currentTime = 0;
        await audioElement.play();
      } catch (error) {
        console.debug('Audio playback failed:', error.message);
      }
    };

    if (prevAgentStatus.current !== agentStatus && enableSoundEffects && !isEnvironmentOffline) {
      if (agentStatus === 'thinking') {
        playSound(processingSound);
      }
      // Only play done sound when she's about to speak, not when she finishes
      if (agentStatus === 'speaking') {
        playSound(doneSound);
      }
      // Play beep when she stops listening and goes idle
      if (agentStatus === 'idle') {
        playSound(beepSound);
      }
      prevAgentStatus.current = agentStatus;
    }
  }, [agentStatus, enableSoundEffects, isEnvironmentOffline]);

  // Placeholder for streaming TTS (partial response playback)
  // If your backend supports streaming partial responses, you can do:
  // function handlePartialResponse(partialText) {
  //   fetch('/api/v1/text-to-speech', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: partialText }) })
  //     .then(res => res.json())
  //     .then(ttsData => playAudio(ttsData.audioContent));
  // }
  // Then, as you receive each partial chunk, call handlePartialResponse(chunk).

  // Inline AnimatedResponse effects (moved from AnimatedResponse.js)
  useEffect(() => {
    function cleanupAnimatedTimeouts() {
      animatedTimeoutsRef.current.forEach(clearTimeout);
      animatedTimeoutsRef.current = [];
    }

    const responseData = lastAgentMessage;
    const { text } = responseData || {};
    const targetElement = animatedResponseRef.current;
    const showFullTextMode = (!isSpeaking && !isThinking) || isListening;

    if (!targetElement || !text) {
      if(targetElement) targetElement.innerHTML = '';
      return;
    }

    if (showFullTextMode) {
      // Show full text without animation - keep same structure to prevent width jump
      targetElement.innerHTML = `<span class="word visible">${text}</span>`;
      return;
    }

    if (lastAnimatedTextRef.current === text) {
      return;
    }
    
    lastAnimatedTextRef.current = text;

    const tokens = text.split(/(\s+)/);
    targetElement.innerHTML = tokens
      .map((token, index) => {
        if (/^\s+$/.test(token)) {
          return token;
        } else {
          return `<span class="word" id="word-${index}">${token}</span>`;
        }
      })
      .join('');

    console.log('🎬 Starting word-by-word animation:', {
      wordCount: tokens.filter(t => !/^\s+$/.test(t)).length,
      fullText: text.substring(0, 50) + '...'
    });

    // Determine if this is text-only (typed) or with audio (spoken)
    const hasAudioContent = responseData && responseData.audioContent;
    
    // Base timing constants
    const BASE_WORD_INTERVAL = 200; // Base word display interval in ms
    const BASE_SCROLL_DIVISOR = 88.209; // Base scroll speed divisor
    
    // Apply 2x speed multiplier for text-only (no audio) responses
    const speedMultiplier = hasAudioContent ? 1 : 2;
    const interval = BASE_WORD_INTERVAL / speedMultiplier; // 200ms with audio, 100ms without
    const scrollDivisor = BASE_SCROLL_DIVISOR / speedMultiplier; // 88.209 with audio, 44.1045 without
    
    console.log('⚙️ Animation settings:', {
      hasAudio: hasAudioContent,
      speedMultiplier: speedMultiplier,
      wordInterval: interval + 'ms',
      scrollDivisor: scrollDivisor
    });

    // Scroll to make the response bubble visible at the top when animation starts
    // Do this synchronously with a longer delay BEFORE word animations begin
    const initialScrollDelay = 350; // Increased delay by 250ms to ensure DOM is ready
    const wordAnimationDelay = initialScrollDelay + 50; // Start word animations AFTER initial scroll
    
    setTimeout(() => {
      if (animatedResponseRef.current && conversationScrollRef.current) {
        // Get the response bubble element (parent of animatedResponseRef)
        const responseBubble = animatedResponseRef.current.closest('.conversation-bubble');
        if (responseBubble) {
          const container = conversationScrollRef.current;
          
          // Get the position of the bubble relative to the scroll container
          const containerRect = container.getBoundingClientRect();
          const bubbleRect = responseBubble.getBoundingClientRect();
          
          // Calculate how much the bubble is offset from the top of the container
          const relativeTop = bubbleRect.top - containerRect.top;
          
          // Calculate target scroll position with offset
          // Instead of trying to scroll to a negative position, calculate from scrollHeight
          const desiredOffsetFromTop = 50; // How far from top we want the bubble
          const currentOffsetFromTop = relativeTop;
          const scrollAdjustment = currentOffsetFromTop - desiredOffsetFromTop;
          const targetScrollTop = Math.max(0, container.scrollTop + scrollAdjustment);
          
          console.log('🎯 Initial scroll debug:', {
            containerScrollTop: container.scrollTop,
            containerScrollHeight: container.scrollHeight,
            containerHeight: containerRect.height,
            bubbleTop: bubbleRect.top,
            containerTop: containerRect.top,
            relativeTop: relativeTop,
            currentOffsetFromTop: currentOffsetFromTop,
            desiredOffsetFromTop: desiredOffsetFromTop,
            scrollAdjustment: scrollAdjustment,
            targetScrollTop: targetScrollTop,
            willBeClamped: targetScrollTop === 0
          });
          
          // Scroll to position the bubble near the top
          container.scrollTo({
            top: targetScrollTop,
            behavior: 'auto'
          });
          
          // Log actual position after scroll
          setTimeout(() => {
            const newRelativeTop = responseBubble.getBoundingClientRect().top - container.getBoundingClientRect().top;
            console.log('📍 After initial scroll:', {
              actualScrollTop: container.scrollTop,
              targetScrollTop: targetScrollTop,
              bubbleOffsetFromTop: newRelativeTop,
              scrollWorked: Math.abs(container.scrollTop - targetScrollTop) < 1
            });
          }, 10);
        }
      }
    }, initialScrollDelay);

    const wordElements = targetElement.querySelectorAll('.word');
    
    // Track the scroll position - will be set after initial scroll completes
    let lastKnownScrollTop = 0;
    
    // Don't auto-scroll for the first ~2 words - let them render in the visible area
    // that we already positioned at the top with the initial scroll
    const noScrollGracePeriod = 2; // 200ms worth of words (2 words for text-only at 100ms/word, 1 word for audio at 200ms/word)
    
    wordElements.forEach((span, i) => {
      // Add wordAnimationDelay to all word timeouts so they start AFTER initial scroll
      const timeout = setTimeout(() => {
        span.classList.add('visible');
        // Scroll down as text animates to keep it in view - use 'auto' to keep pace with animation
        requestAnimationFrame(() => {
          if (conversationScrollRef.current && animatedResponseRef.current) {
            const container = conversationScrollRef.current;
            const responseElement = animatedResponseRef.current;
            
            // Initialize lastKnownScrollTop on first word
            if (i === 0) {
              lastKnownScrollTop = container.scrollTop;
              console.log('🔄 Initialized scroll tracking at:', lastKnownScrollTop);
            }
            
            // Check if scroll position changed unexpectedly
            const scrollDelta = Math.abs(container.scrollTop - lastKnownScrollTop);
            if (scrollDelta > 5 && i > 0) {
              console.warn(`⚠️ Unexpected scroll change at word ${i}:`, {
                lastKnownScrollTop: lastKnownScrollTop,
                currentScrollTop: container.scrollTop,
                delta: container.scrollTop - lastKnownScrollTop
              });
            }
            
            // Skip auto-scrolling for the first few words - we already positioned at top
            if (i < noScrollGracePeriod) {
              lastKnownScrollTop = container.scrollTop;
              if (i % 5 === 0) {
                console.log(`⏭️ Word ${i} - skipping auto-scroll (grace period)`);
              }
              return;
            }
            
            // On the first word after grace period, do a smooth scroll to position
            if (i === noScrollGracePeriod) {
              // Get bounding rectangles for accurate positioning
              const containerRect = container.getBoundingClientRect();
              const responseRect = responseElement.getBoundingClientRect();
              
              // Calculate the bottom of the response relative to the container's top
              const responseBottomRelative = responseRect.bottom - containerRect.top;
              const containerHeight = containerRect.height;
              
              const willScroll = responseBottomRelative > containerHeight;
              if (willScroll) {
                // Apply dynamic scroll divisor (faster for text-only, slower for audio)
                const scrollAmount = (responseBottomRelative - containerHeight + 20) / scrollDivisor;
                console.log(`🎯 Grace period ended at word ${i} - smooth scrolling to position`);
                container.scrollTo({
                  top: container.scrollTop + scrollAmount,
                  behavior: 'smooth'
                });
                lastKnownScrollTop = container.scrollTop + scrollAmount;
              }
              return;
            }
            
            // Get bounding rectangles for accurate positioning
            const containerRect = container.getBoundingClientRect();
            const responseRect = responseElement.getBoundingClientRect();
            
            // Calculate the bottom of the response relative to the container's top
            const responseBottomRelative = responseRect.bottom - containerRect.top;
            const containerHeight = containerRect.height;
            
            const willScroll = responseBottomRelative > containerHeight;
            // Apply dynamic scroll divisor (faster for text-only, slower for audio)
            // Add 3% extra to ensure we reach the bottom properly
            const scrollAmount = willScroll ? ((responseBottomRelative - containerHeight + 20) / scrollDivisor) * 1.03 : 0;
            
            if (i % 5 === 0) { // Log every 5th word to avoid console spam
              console.log(`📝 Word ${i} scroll debug:`, {
                wordIndex: i,
                containerScrollTop: container.scrollTop,
                responseBottom: responseRect.bottom,
                containerTop: containerRect.top,
                responseBottomRelative: responseBottomRelative,
                containerHeight: containerHeight,
                willScroll: willScroll,
                scrollAmount: scrollAmount,
                targetScrollTop: willScroll ? container.scrollTop + scrollAmount : container.scrollTop
              });
            }
            
            // Only scroll if the new content is going below the visible area
            if (willScroll) {
              const beforeScroll = container.scrollTop;
              container.scrollTo({
                top: container.scrollTop + scrollAmount,
                behavior: 'auto'
              });
              lastKnownScrollTop = container.scrollTop;
              
              if (i % 5 === 0) {
                console.log(`✏️ Word ${i} scrolled:`, {
                  before: beforeScroll,
                  after: container.scrollTop,
                  amount: scrollAmount
                });
              }
            } else {
              lastKnownScrollTop = container.scrollTop;
            }
          }
        });
      }, wordAnimationDelay + (i * interval)); // Start word animations AFTER initial scroll
      animatedTimeoutsRef.current.push(timeout);
    });

    // Final smooth scroll to bottom after all words are visible
    const finalScrollDelay = wordAnimationDelay + (wordElements.length * interval) + 300;
    const finalScrollTimeout = setTimeout(() => {
      if (conversationScrollRef.current) {
        console.log('🏁 Final smooth scroll to bottom');
        conversationScrollRef.current.scrollTo({
          top: conversationScrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, finalScrollDelay);
    animatedTimeoutsRef.current.push(finalScrollTimeout);

    return () => {
      cleanupAnimatedTimeouts();
    };
  }, [lastAgentMessage, isSpeaking, isThinking, isListening, scrollToBottom]);

  // Check if user has scrolled down to show/hide blur overlay
  useEffect(() => {
    const checkScrollPosition = () => {
      if (conversationScrollRef.current) {
        // Show blur if we're not at the top (scrolled down more than 5px)
        const isScrolledDown = conversationScrollRef.current.scrollTop > 5;
        setHasScrollableContent(isScrolledDown);
      }
    };

    // Check initially
    checkScrollPosition();

    // Add scroll listener
    const scrollContainer = conversationScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollPosition);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [agentMessages, lastAgentMessage]);

  // If the agent channel is configured with a plain iframe URL
  // (channelConfig.agent.url), render that instead of the live agent UI.
  if (agentConfig?.url) {
    return (
      <iframe
        src={agentConfig.url}
        title="Agent View"
        className="w-full h-full"
        style={{ border: 'none', display: 'block', backgroundColor: '#fff' }}
      />
    );
  }

  return (
    <>
      <AgentViewStyles 
        gradientColor1={gradientColor1} 
        gradientColor2={gradientColor2} 
        gradientColor3={gradientColor3} 
        gradientColor4={gradientColor4} 
        backgroundColor={backgroundColor} 
        conversationBubbleBackgroundColor={conversationBubbleBackgroundColor}
        conversationBubbleForegroundColor={conversationBubbleForegroundColor}
      />
      {agentFontFamily && (
        <style>{`.agent-view-root { font-family: ${agentFontFamily} !important; }`}</style>
      )}
      <div className="agent-view-root agent-view-glass h-full flex flex-col items-center relative">
        {/* Unified conversation view - always visible */}
        <div className="flex-1 w-full flex flex-col overflow-hidden relative" style={{ paddingTop: '100px', paddingBottom: '100px' }}>
          {/* Header */}
          <div className="w-full flex flex-col items-center pt-6 pb-2 px-6" style={{ flexShrink: 0, position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20, background: backgroundColor || 'linear-gradient(to bottom, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 80%, transparent 100%)' }}>
            <Header gradientColor1={gradientColor1} gradientColor2={gradientColor2} gradientColor3={gradientColor3} foregroundColor={foregroundColor} />
            {/* Soft blur fade overlay - only shown when there's scrollable content */}
            {hasScrollableContent && <div className="conversation-fade-overlay"></div>}
          </div>

          {/* Unified scrollable conversation area */}
          <div
            ref={conversationScrollRef}
            className="flex-1 px-4 pb-4 space-y-4 no-scrollbar"
            style={{
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              position: 'relative',
            }}
          >
            {/* Soft blur fade at top */}
            {/* Show conversation history if exists */}
            {agentMessages.length > 0 && agentMessages.map((msg, i) => {
              const isAgent = msg.sender === 'agent';
              const isLastMessage = i === agentMessages.length - 1;
              
              return (
                <div
                  key={i}
                  className={`w-full animate-fade-in ${isAgent ? 'flex justify-start' : 'flex justify-end'}`}
                  style={{ 
                    margin: '18px 0',
                    paddingRight: isAgent ? '64px' : '0',
                    paddingLeft: isAgent ? '0' : '64px',
                  }}
                >
                  {isAgent && isLastMessage && isSpeaking ? (
                    // Current agent response with animation - left aligned
                    <div
                      className="conversation-bubble px-5 py-3 shadow-xl text-base font-medium text-slate-100"
                      style={{
                        opacity: 1,
                        transform: 'translateY(0)',
                        maxWidth: '100%',
                        width: '100%',
                      }}
                    >
                      <div style={{ textAlign: 'left' }}>
                        <div className="sender-name flex items-center text-xs font-semibold mb-1 opacity-70">
                          <SmallAgentOrb isThinking={false} logoSrc={agentConfig?.avatar} />
                          {agentTitle}
                        </div>
                        <div 
                          ref={animatedResponseRef} 
                          className="agent-animated-text text-base font-medium text-slate-100 leading-relaxed" 
                          style={{ 
                            whiteSpace: 'pre-wrap', 
                            fontSize: '1rem', 
                            textAlign: 'left',
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    // Regular message bubble - aligned based on sender
                    <div
                      className={`conversation-bubble px-5 py-3 shadow-xl text-base font-medium text-slate-100 ${!isAgent ? 'user-bubble' : ''}`}
                      style={{
                        opacity: 1,
                        transform: 'translateY(0)',
                        maxWidth: '100%',
                        width: '100%',
                      }}
                    >
                      <div style={{ textAlign: isAgent ? 'left' : 'right' }}>
                        {isAgent ? (
                          <div className="sender-name flex items-center text-xs font-semibold mb-1 opacity-70">
                            <SmallAgentOrb isThinking={false} logoSrc={agentConfig?.avatar} />
                            {agentTitle}
                          </div>
                        ) : (
                          <span className="sender-name block text-xs font-semibold mb-1 opacity-70">
                            You
                          </span>
                        )}
                        <span className="text-base leading-relaxed whitespace-pre-line">{msg.text}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Show listening status as a temporary message bubble - right aligned like user messages */}
            {isListening && (
              <div
                className="w-full animate-fade-in flex justify-end"
                style={{ margin: '18px 0', paddingLeft: '64px' }}
              >
                <div
                  className="conversation-bubble user-bubble px-5 py-3 shadow-xl text-base font-medium text-slate-100"
                  style={{
                    opacity: 0.9,
                    transform: 'translateY(0)',
                    maxWidth: '100%',
                    width: '100%',
                  }}
                >
                  <div style={{ textAlign: 'right' }}>
                    <span className="sender-name block text-xs font-semibold mb-1 opacity-70">
                      You
                    </span>
                    <span className="text-base leading-relaxed inline-flex items-center justify-end">
                      Listening
                      <span className="ml-2 inline-flex space-x-1">
                        <span className="animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s' }}>.</span>
                        <span className="animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1s' }}>.</span>
                        <span className="animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1s' }}>.</span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Show thinking status as a temporary message bubble */}
            {isThinking && (
              <div
                className="w-full animate-fade-in flex justify-start"
                style={{ margin: '18px 0', paddingRight: '64px' }}
              >
                <div
                  className="conversation-bubble px-5 py-3 shadow-xl text-base font-medium text-slate-100"
                  style={{
                    opacity: 0.8,
                    transform: 'translateY(0)',
                    maxWidth: '100%',
                    width: '100%',
                  }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <div className="sender-name flex items-center text-xs font-semibold mb-1 opacity-70">
                      <SmallAgentOrb isThinking={true} logoSrc={agentConfig?.avatar} />
                      {agentTitle}
                    </div>
                    <span className="text-base leading-relaxed flex items-center">
                      {thinkingStatus}
                      <span className="ml-2 inline-flex space-x-1">
                        <span className="animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s' }}>.</span>
                        <span className="animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1s' }}>.</span>
                        <span className="animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1s' }}>.</span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer with text input and 3 stacked buttons */}
        <div className="footer-glass fixed left-0 right-0 bottom-0 w-full flex flex-col items-center justify-center" style={{ zIndex: 40 }}>
          {permissionError && <p className="text-red-400 text-sm mb-2">Microphone access is required. Please enable it in your browser settings.</p>}
          <div className="w-full flex flex-row items-stretch gap-2 px-4">
            {/* Text input field - textarea for top alignment */}
            <textarea
              ref={textareaRef}
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (textInput.trim()) {
                    getTextResponse(textInput);
                    setTextInput('');
                  }
                }
              }}
              placeholder={isListening ? "Listening..." : isThinking ? "Agent is thinking..." : isSpeaking ? "Agent is responding..." : "Type your message..."}
              rows={1}
              disabled={isListening || isThinking}
              className="flex-1 text-slate-100 rounded-xl px-4 py-2.5 focus:outline-none resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
                fontSize: '15px',
                minHeight: '100%',
                lineHeight: '1.5',
                verticalAlign: 'top',
                transition: 'all 0.2s ease',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--agent-primary, #8b5cf6)';
                e.target.style.boxShadow = `0 0 0 3px rgba(139, 92, 246, 0.15), inset 0 2px 4px rgba(0, 0, 0, 0.1)`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
            />
            
            {/* Vertical button stack - WhatsApp style: show send when text exists, mic when empty */}
            <div className="flex flex-col gap-1.5">
              {/* Send button - always shown when there's text input, disabled when thinking */}
              {textInput.trim() && (
                <button
                  className="nav-button"
                  aria-label={isAnimatingText ? "Fast Forward and Send" : "Send message"}
                  onClick={() => {
                    if (textInput.trim() && !isThinking) {
                      // If agent is currently animating text, stop the animation first
                      if (isAnimatingText) {
                        setShowFullText(true);
                        dispatch(setAgentStatus({ agentStatus: 'idle' }));
                        // Clean up any animation timeouts
                        if (animatedTimeoutsRef.current) {
                          animatedTimeoutsRef.current.forEach(clearTimeout);
                          animatedTimeoutsRef.current = [];
                        }
                        // Small delay to let animation stop before sending
                        setTimeout(() => {
                          getTextResponse(textInput);
                          setTextInput('');
                        }, 100);
                      } else {
                        // Normal send if not animating
                        getTextResponse(textInput);
                        setTextInput('');
                      }
                    }
                  }}
                  disabled={isThinking}
                  style={{
                    opacity: isThinking ? 0.5 : 1,
                    cursor: isThinking ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isAnimatingText ? (
                    <span className="material-icons-outlined text-white" style={{ fontSize: 20 }}>fast_forward</span>
                  ) : (
                    <span className="material-icons text-white" style={{ fontSize: 20 }}>send</span>
                  )}
                </button>
              )}
              
              {/* Microphone button - always shown when input is empty, disabled when thinking */}
              {!textInput.trim() && (
                <MicrophoneButton
                  onStart={handleStartRecording}
                  onStop={stopRecording}
                  isRecording={isRecording}
                  isSpeaking={isSpeaking}
                  isThinking={isThinking}
                  isListening={isListening}
                  isPlaying={isSpeaking && lastAgentMessage && lastAgentMessage.audioContent && !showFullText}
                  onStopAudio={handleStopAudio}
                  isAnimatingText={isAnimatingText}
                  onFastForward={() => {
                    setShowFullText(true);
                    dispatch(setAgentStatus({ agentStatus: 'idle' }));
                    // Clean up any animation timeouts
                    if (animatedTimeoutsRef.current) {
                      animatedTimeoutsRef.current.forEach(clearTimeout);
                      animatedTimeoutsRef.current = [];
                    }
                  }}
                />
              )}
              
              {/* Reset button - always shown but disabled when listening or thinking */}
              <button
                className="nav-button"
                aria-label="New Conversation"
                onClick={() => !isListening && !isThinking && setShowResetConfirmation(true)}
                disabled={isListening || isThinking}
                style={{
                  opacity: (isListening || isThinking) ? 0.5 : 1,
                  cursor: (isListening || isThinking) ? 'not-allowed' : 'pointer'
                }}
              >
                <RestartAltIcon sx={{ fontSize: 20, color: 'white' }} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Reset Confirmation Modal */}
        {showResetConfirmation && (
          <div 
            className="fixed inset-0 flex items-center justify-center"
            style={{ 
              zIndex: 9999,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)'
            }}
          >
            <div 
              className="bg-slate-800 rounded-2xl shadow-2xl p-6 mx-4"
              style={{
                maxWidth: '400px',
                width: '90%',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >

              <p className="text-slate-300 mb-6 text-center text-lg">
                Are you sure you want to restart your conversation?
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-8 py-3 rounded-lg font-medium transition-all text-lg"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                >
                  No
                </button>
                <button
                  onClick={async () => {
                    if (pegaConversationID) {
                      try { 
                        await closeConversation(agentConfig.agentID, pegaConversationID, configId); 
                      } catch (e) { 
                        /* ignore */ 
                      }
                    }
                    dispatch(resetAgent());
                    setShowFullText(false);
                    setTextInput('');
                    setShowResetConfirmation(false);
                  }}
                  className="px-8 py-3 rounded-lg font-medium transition-all text-lg"
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}